import React, { useState, useRef, useEffect } from 'react';
import { gsap } from 'gsap';

const AudioPlayer = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [volume, setVolume] = useState(0.4);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const audioRef = useRef(null);
  const playerRef = useRef(null);
  const titleRef = useRef(null);
  const volumeRef = useRef(null);
  const progressRef = useRef(null);
  const timeoutRef = useRef(null);
  const expandTimeoutRef = useRef(null);

  // Format time helper
  const formatTime = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Événements audio
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleLoadStart = () => {
      setIsLoading(true);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);

    // Auto-play au chargement avec délai
    const autoPlay = setTimeout(() => {
      const playAudio = async () => {
        try {
          await audio.play();
          
          // Fade in progressif du volume
          audio.volume = 0;
          gsap.to(audio, {
            volume: volume,
            duration: 3,
            ease: "sine.inOut"
          });
          
          setIsPlaying(true);
        } catch (error) {
          console.log("Auto-play blocked:", error);
          // Fallback: attendre une interaction utilisateur
          const handleFirstInteraction = () => {
            audio.play();
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('touchstart', handleFirstInteraction);
          };
          document.addEventListener('click', handleFirstInteraction);
          document.addEventListener('touchstart', handleFirstInteraction);
        }
      };

      playAudio();
    }, 1500);

    // Animation d'entrée discrète
    gsap.fromTo(playerRef.current,
      { y: 80, opacity: 0, scale: 0.8 },
      { 
        y: 0, 
        opacity: 1, 
        scale: 1, 
        duration: 1.2, 
        delay: 1, 
        ease: "back.out(1.7)" 
      }
    );

    return () => {
      clearTimeout(autoPlay);
      clearTimeout(timeoutRef.current);
      clearTimeout(expandTimeoutRef.current);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
    };
  }, [volume]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    try {
      if (isPlaying) {
        audio.pause();
      } else {
        await audio.play();
      }
      setIsPlaying(!isPlaying);
    } catch (error) {
      console.log("Audio play failed:", error);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const handleProgressClick = (e) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const rect = progressRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleMouseEnter = () => {
    clearTimeout(timeoutRef.current);
    setShowTitle(true);
    
    // Animation du titre
    if (titleRef.current) {
      gsap.killTweensOf(titleRef.current);
      gsap.to(titleRef.current, {
        opacity: 1,
        x: 0,
        duration: 0.4,
        ease: "power2.out"
      });
    }

    // Animation du volume slider
    if (volumeRef.current && isExpanded) {
      gsap.fromTo(volumeRef.current,
        { scaleX: 0, opacity: 0 },
        { scaleX: 1, opacity: 1, duration: 0.3, ease: "back.out(1.5)" }
      );
    }
  };

  const handleMouseLeave = () => {
    // Délai avant de cacher le titre
    timeoutRef.current = setTimeout(() => {
      if (titleRef.current) {
        gsap.to(titleRef.current, {
          opacity: 0,
          x: 10,
          duration: 0.3,
          ease: "power2.in",
          onComplete: () => setShowTitle(false)
        });
      }

      // Réduire automatiquement après un délai
      expandTimeoutRef.current = setTimeout(() => {
        if (isExpanded) {
          setIsExpanded(false);
        }
      }, 2000);
    }, 500);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    clearTimeout(expandTimeoutRef.current);
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div 
      ref={playerRef}
      className="audio-player fixed bottom-6 right-6 z-50"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Audio element caché */}
      <audio
        ref={audioRef}
        loop
        preload="metadata"
      >
        <source src="/assets/Chocolate-and-Vanilla.mp3" type="audio/mpeg" />
        Votre navigateur ne supporte pas l'élément audio.
      </audio>

      {/* Conteneur principal */}
      <div className={`relative flex items-center transition-all duration-500 ${
        isExpanded ? 'bg-black/80 backdrop-blur-xl rounded-2xl p-4 shadow-2xl' : ''
      }`}>
        
        {/* Panneau étendu */}
        {isExpanded && (
          <div className="mr-4 space-y-3 min-w-[200px]">
            {/* Titre de la musique */}
            <div className="text-white font-medium text-sm">Chocolate and Vanilla (Feat Safah Shisey)</div>
            
            {/* Barre de progression */}
            <div 
              ref={progressRef}
              className="w-full h-1 bg-white/20 rounded-full cursor-pointer group"
              onClick={handleProgressClick}
            >
              <div 
                className="h-full bg-gradient-to-r from-green-400 to-blue-400 rounded-full relative group-hover:from-green-300 group-hover:to-blue-300 transition-all duration-300"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute right-0 top-1/2 w-3 h-3 bg-white rounded-full transform -translate-y-1/2 translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg" />
              </div>
            </div>
            
            {/* Temps */}
            <div className="flex justify-between text-white/60 text-xs">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
            
            {/* Contrôle du volume */}
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-white/60" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.784L4.728 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.728l3.655-3.784a1 1 0 011.617.784zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
              </svg>
              <input
                ref={volumeRef}
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                className="w-full h-1 bg-white/20 rounded-full appearance-none cursor-pointer slider [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white"
              />
            </div>
          </div>
        )}

        {/* Titre qui apparaît au hover (mode réduit) */}
        {!isExpanded && (
          <div 
            ref={titleRef}
            className="absolute right-14 bg-black/80 backdrop-blur-lg rounded-xl px-3 py-2 border border-white/10 shadow-xl transition-all duration-300 pointer-events-none"
            style={{ 
              opacity: showTitle ? 1 : 0,
              transform: showTitle ? 'translateX(0)' : 'translateX(10px)'
            }}
          >
            <div className="text-white text-sm font-medium whitespace-nowrap">
              Chocolate and Vanilla (Feat Safah Shisey)
            </div>
            <div className="text-white/60 text-xs mt-1 flex items-center">
              {isLoading ? (
                <div className="flex space-x-1">
                  <div className="w-1 h-3 bg-white/40 rounded-sm animate-pulse" />
                  <div className="w-1 h-3 bg-white/40 rounded-sm animate-pulse delay-75" />
                  <div className="w-1 h-3 bg-white/40 rounded-sm animate-pulse delay-150" />
                </div>
              ) : (
                <>{isPlaying ? 'En lecture' : 'En pause'} • {formatTime(currentTime)}</>
              )}
            </div>
          </div>
        )}

        {/* Bouton principal */}
        <div className="relative">
          <button
            onClick={togglePlay}
            className="group flex items-center justify-center w-12 h-12 bg-black/60 backdrop-blur-lg rounded-full border border-white/20 shadow-2xl transition-all duration-300 hover:bg-black/80 hover:border-white/30 hover:scale-110 active:scale-105"
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
            ) : isPlaying ? (
              <div className="flex space-x-1">
                <div className="w-1 h-3 bg-white/90 rounded-sm group-hover:bg-white transition-all duration-300 group-hover:scale-y-125" />
                <div className="w-1 h-3 bg-white/90 rounded-sm group-hover:bg-white transition-all duration-300 group-hover:scale-y-125 delay-75" />
              </div>
            ) : (
              <div className="w-0 h-0 border-t-[6px] border-b-[6px] border-l-[10px] border-transparent border-l-white/90 group-hover:border-l-white ml-0.5 transition-all duration-300 group-hover:scale-110" />
            )}
            
            {/* Indicateur de lecture */}
            <div className={`absolute -top-1 -right-1 w-3 h-3 rounded-full bg-gradient-to-r from-green-400 to-blue-400 ${
              isPlaying ? 'animate-pulse' : 'opacity-0'
            } transition-all duration-300 shadow-lg`} />
          </button>

          {/* Bouton expand */}
          <button
            onClick={toggleExpanded}
            className={`absolute -bottom-2 -right-2 w-5 h-5 bg-black/80 backdrop-blur-sm rounded-full border border-white/20 flex items-center justify-center transition-all duration-300 hover:bg-black hover:scale-110 ${
              isExpanded ? 'rotate-45 bg-white/20' : ''
            }`}
          >
            <div className="w-3 h-0.5 bg-white/70 rounded-full" />
            {!isExpanded && (
              <div className="absolute w-0.5 h-3 bg-white/70 rounded-full" />
            )}
          </button>
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
        
        .slider::-moz-range-thumb {
          height: 12px;
          width: 12px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        }
      `}</style>
    </div>
  );
};

export default AudioPlayer;